import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "../../api/httpClient";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import { InviteModal } from "../../components/modal";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { FiEdit2, FiTrash2, FiUploadCloud, FiFileText } from "react-icons/fi";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const MEDIA_CACHE_KEY = "cehr_company_media_v1";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatFileSize = (bytes) => {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) {
    return "-";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kb = bytes / 1024;

  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`;
  }

  return `${(kb / 1024).toFixed(1)} MB`;
};

const isImageMedia = (item = {}) => {
  if (
    typeof item.fileMimeType === "string" &&
    item.fileMimeType.startsWith("image/")
  ) {
    return true;
  }

  if (typeof item.fileName === "string") {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(item.fileName);
  }

  return false;
};

const MediaModal = ({
  isOpen,
  onClose,
  title,
  isSubmitting,
  onSubmit,
  errorMessage,
  form,
  setForm,
  selectedFile,
  setSelectedFile,
}) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState("");

  const validateAndSelectFile = (file) => {
    if (!file) {
      setSelectedFile(null);
      setFileError("");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError("File must not be larger than 2MB.");
      setSelectedFile(null);
      return;
    }

    setFileError("");
    setSelectedFile(file);
  };

  const handleFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;
    validateAndSelectFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const file = event.dataTransfer?.files?.[0] || null;
    validateAndSelectFile(file);
  };

  const submitHandler = (event) => {
    event.preventDefault();
    onSubmit();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <InviteModal
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="my-4 px-2 font-Nunito">
        <p className="text-2xl font-semibold text-black">{title}</p>

        <form
          className="mt-8 space-y-5"
          onSubmit={submitHandler}
        >
          {errorMessage ? (
            <p
              className="text-sm text-red-600"
              role="alert"
            >
              {errorMessage}
            </p>
          ) : null}

          <div>
            <label className="block mb-2 font-[500] text-[14px] text-black">
              Media Name
            </label>
            <input
              type="text"
              placeholder="Media title"
              value={form.mediaName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  mediaName: event.target.value,
                }))
              }
              className="w-full p-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#E9ECEF]"
            />
          </div>

          <div>
            <label className="block mb-2 font-[500] text-[14px] text-black">
              Description
            </label>
            <textarea
              rows={4}
              placeholder="Describe this media"
              value={form.description}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              className="w-full p-3 rounded-xl focus:outline-[#1234CD] border-2 border-[#E9ECEF] resize-none"
            />
          </div>

          <div>
            <p className="font-medium text-sm text-black mb-2">
              Choose File (Not more than 2MB)
            </p>
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  inputRef.current?.click();
                }
              }}
              onClick={() => inputRef.current?.click()}
              onDragEnter={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${
                dragActive
                  ? "border-[#1234CD] bg-[#EEF1FF]"
                  : "border-[#D7DBE0] bg-[#F8FAFB]"
              }`}
            >
              <FiUploadCloud
                className="mx-auto mb-3 text-[#1234CD]"
                size={28}
              />
              <p className="text-sm text-[#4B5563]">
                Drag and drop media file here, or{" "}
                <span className="text-[#1234CD] font-semibold">
                  choose from your device
                </span>
              </p>
              {selectedFile ? (
                <p className="mt-3 text-xs text-gray-700">
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              ) : null}
            </div>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
            {fileError ? (
              <p className="mt-2 text-xs text-red-600">{fileError}</p>
            ) : null}
          </div>

          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="font-[700] text-sm w-44 rounded-xl text-white py-3 bg-[#1234CD] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </InviteModal>
  );
};

const Media = () => {
  const apiBaseUrl = getApiBaseUrl();
  const cachedData = useMemo(
    () => readSessionCache(MEDIA_CACHE_KEY, { mediaItems: [] }),
    [],
  );
  const [isLoading, setIsLoading] = useState(!cachedData?.mediaItems?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mediaItems, setMediaItems] = useState(cachedData?.mediaItems || []);
  const [errorMessage, setErrorMessage] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [form, setForm] = useState({ mediaName: "", description: "" });

  const resolveErrorMessage = (error, fallback) => {
    const message = error?.response?.data?.message || fallback;
    return Array.isArray(message) ? message.join(", ") : message;
  };

  const loadMedia = useCallback(async () => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      if (mediaItems.length === 0) {
        setIsLoading(true);
      }
      setErrorMessage("");

      const response = await axios.get(`${apiBaseUrl}/organizations/media`);
      const nextMediaItems = response?.data?.media || [];
      setMediaItems(nextMediaItems);
      writeSessionCache(MEDIA_CACHE_KEY, { mediaItems: nextMediaItems });
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "Unable to fetch media."));
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadMedia();
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadMedia]);

  const resetForm = () => {
    setForm({ mediaName: "", description: "" });
    setSelectedFile(null);
    setEditingMedia(null);
  };

  const openCreateModal = () => {
    resetForm();
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingMedia(item);
    setForm({
      mediaName: item.mediaName || "",
      description: item.description || "",
    });
    setSelectedFile(null);
    setErrorMessage("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
    setErrorMessage("");
  };

  const submitMedia = async () => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (!form.mediaName.trim() || !form.description.trim()) {
      setErrorMessage("Media name and description are required.");
      return;
    }

    if (!editingMedia && !selectedFile) {
      setErrorMessage("Please upload a media file.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      const payload = new FormData();
      payload.append("mediaName", form.mediaName.trim());
      payload.append("description", form.description.trim());

      if (selectedFile) {
        payload.append("file", selectedFile);
      }

      if (editingMedia?.id) {
        await axios.patch(
          `${apiBaseUrl}/organizations/media/${editingMedia.id}`,
          payload,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
        await axios.post(`${apiBaseUrl}/organizations/media`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      closeModal();
      await loadMedia();
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "Unable to save media."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (item) => {
    setSelectedMedia(item);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedMedia(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (!apiBaseUrl || !selectedMedia?.id) {
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.delete(
        `${apiBaseUrl}/organizations/media/${selectedMedia.id}`,
      );
      closeDeleteModal();
      await loadMedia();
    } catch (error) {
      setErrorMessage(resolveErrorMessage(error, "Unable to delete media."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPreviewModal = (item, fileUrl) => {
    setPreviewMedia({
      title: item?.mediaName || "Media preview",
      url: fileUrl,
    });
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewMedia(null);
  };

  const emptyStateText = useMemo(() => {
    if (isLoading) {
      return "";
    }

    return "No media uploaded yet. Add media to get started.";
  }, [isLoading]);

  return (
    <div className="font-Nunito p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Media" },
        ]}
        title="Media"
        subtitle="Upload and manage company media assets."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openCreateModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      {errorMessage ? (
        <p
          className="mb-4 text-sm text-red-600"
          role="alert"
        >
          {errorMessage}
        </p>
      ) : null}

      {isLoading && mediaItems.length === 0 ? (
        <FetchLoadingState message="Loading media..." />
      ) : null}

      {!isLoading && mediaItems.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 border border-dashed border-[#D7DBE0] rounded-xl">
          {emptyStateText}
        </div>
      ) : null}

      {mediaItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {mediaItems.map((item) => {
            const fileUrl = buildAssetUrl(item.fileUrl);
            const canPreviewImage = fileUrl && isImageMedia(item);

            return (
              <article
                key={item.id}
                className="rounded-xl border border-[#E4E7EC] bg-white p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 line-clamp-1">
                      {item.mediaName}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Created: {formatDate(item.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600">
                    <button
                      type="button"
                      title="Edit media"
                      onClick={() => openEditModal(item)}
                      className="hover:text-[#1234CD]"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      type="button"
                      title="Delete media"
                      onClick={() => openDeleteModal(item)}
                      className="hover:text-red-600"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>

                <p className="mt-3 text-sm text-gray-600 min-h-[56px] line-clamp-3">
                  {item.description}
                </p>

                <div className="mt-4 rounded-lg border border-[#E9ECEF] p-3 bg-[#F8FAFB]">
                  {canPreviewImage ? (
                    <button
                      type="button"
                      onClick={() => openPreviewModal(item, fileUrl)}
                      className="block w-full"
                      title="Open image preview"
                    >
                      <img
                        src={fileUrl}
                        alt={item.mediaName || "Media preview"}
                        className="h-44 w-full rounded-md object-cover cursor-zoom-in"
                      />
                    </button>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <FiFileText size={16} />
                        <span className="line-clamp-1">
                          {item.fileName || "Uploaded file"}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {item.fileMimeType || "Unknown type"} |{" "}
                        {formatFileSize(item.fileSize)}
                      </div>
                      {fileUrl ? (
                        <a
                          className="inline-block mt-2 text-xs text-[#1234CD] font-medium"
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open file
                        </a>
                      ) : null}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      <MediaModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingMedia ? "Edit Media" : "Create Media"}
        isSubmitting={isSubmitting}
        onSubmit={submitMedia}
        errorMessage={errorMessage}
        form={form}
        setForm={setForm}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
      />

      <InviteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
      >
        <div className="py-10 text-center font-Outfit">
          <p className="text-xl font-semibold text-gray-900">Delete Media</p>
          <p className="text-sm text-gray-600 mt-3">
            Are you sure you want to delete{" "}
            <span className="font-semibold">{selectedMedia?.mediaName}</span>?
          </p>

          <div className="mt-8 flex justify-center gap-3">
            <button
              type="button"
              onClick={closeDeleteModal}
              className="px-5 py-2 rounded-xl border border-[#D1D5DB] text-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleDelete}
              className="px-5 py-2 rounded-xl bg-red-600 text-white disabled:opacity-60"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </InviteModal>

      <InviteModal
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
      >
        <div className="font-Outfit p-2">
          <p className="text-lg font-semibold text-gray-900 mb-4 line-clamp-1">
            {previewMedia?.title}
          </p>

          {previewMedia?.url ? (
            <img
              src={previewMedia.url}
              alt={previewMedia.title || "Media preview"}
              className="max-h-[75vh] w-full rounded-xl object-contain bg-[#F8FAFB]"
            />
          ) : (
            <p className="text-sm text-gray-500">
              Unable to load image preview.
            </p>
          )}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={closePreviewModal}
              className="px-5 py-2 rounded-xl bg-[#1234CD] text-white"
            >
              Close
            </button>
          </div>
        </div>
      </InviteModal>
    </div>
  );
};

export default Media;
